// ─── Mapping targets (Integrations → Mappings tab) ───────────────────────────
//
// What a form-field mapping can write into, and how a saved mapping reads back.
// A target is either a flat field id or, for a cell of an inline `table` field,
// the cell key `${rowId}__${colId}` (`cellKey` in lib/tableForm.ts). The backend
// resolves the same keys in `resolveTableTarget` (cgt-backend
// src/form-engine/table-cells.ts) and composes the same label, so the Mappings
// tab and the Populate dialog agree.
//
// Pure on purpose: `vitest.config.ts` matches only `src/**/*.test.ts`, so logic
// in the component would have no coverage at all.

import type { FieldDefinition, TableCellType, TableConfig } from '@/types/cgt'
import { allFields } from '@/lib/formSections'
import { ADDED_ROWS_KEY, resolveCell } from '@/lib/tableForm'

type DefinitionLike = Parameters<typeof allFields>[0]

/**
 * Flat field types a mapping may write into.
 *
 * `datetime` is left out for flat fields, which predate this set's move here;
 * adding it is its own change. A `datetime` table COLUMN is offered, because
 * column choice goes through `tableColumnOptions`, not this set, and the
 * backend coerces a value to the `YYYY-MM-DDTHH:mm` such a cell stores.
 *
 * `table` is not here either: a table holds no value of its own. It is offered
 * as a container (`isTable`), and the value lands in one of its cells.
 */
export const VALUE_BEARING: ReadonlySet<string> = new Set([
  'text',
  'number',
  'date',
  'textarea',
  'checkbox',
  'dropdown',
  'calculated',
])

/** Cell types that hold no value a mapping could write. */
const NON_VALUE_CELLS: ReadonlySet<TableCellType> = new Set<TableCellType>([
  // Recomputed from its formula, so a written value would be discarded.
  'calculated',
  // A FormAttachment keyed on the cell, not a value in the map.
  'image',
  // Fixed text in the definition.
  'description',
])

export type MappingFieldOption = { id: string; label: string; isTable: boolean }

/** Fields a mapping can target: value-bearing leaves plus inline table fields. */
export function mappingFieldOptions(def: DefinitionLike): MappingFieldOption[] {
  return allFields(def)
    .filter((f) => VALUE_BEARING.has(f.fieldType) || isTableField(f))
    .map((f) => ({ id: f.id, label: f.label, isTable: f.fieldType === 'table' }))
}

function isTableField(f: FieldDefinition): f is FieldDefinition & { tableConfig: TableConfig } {
  return f.fieldType === 'table' && !!f.tableConfig
}

/** The inline table field with this id, or null when it is not a table. */
export function tableFieldById(def: DefinitionLike, fieldId: string): FieldDefinition | null {
  const field = allFields(def).find((f) => f.id === fieldId)
  return field && isTableField(field) ? field : null
}

/** Predefined rows of a table field, in order. Rows added at fill time are not targetable. */
export function tableRowOptions(field: FieldDefinition | null): Array<{ id: string; label: string }> {
  if (!field || !isTableField(field)) return []
  return (field.tableConfig.rows ?? []).map((r) => ({ id: r.id, label: r.label || 'Row' }))
}

/**
 * Columns a mapping can write into for one row.
 *
 * Takes the row because the check runs on the RESOLVED cell type: a per-cell
 * override can make a column writable in one row and not in another.
 */
export function tableColumnOptions(
  field: FieldDefinition | null,
  rowId: string,
): Array<{ id: string; label: string; type: TableCellType }> {
  if (!field || !isTableField(field) || !rowId) return []
  const config = field.tableConfig
  return (config.columns ?? [])
    .map((col) => ({
      id: col.id,
      label: col.label || 'Column',
      type: resolveCell(config, rowId, col).type ?? 'text',
    }))
    .filter((c) => !NON_VALUE_CELLS.has(c.type))
}

const SEP = '__'

/** `r__c` is a cell key; a plain id, `r__` and the added-rows key are not. */
export function splitCellKey(key: string): { rowId: string; colId: string } | null {
  const sep = key.indexOf(SEP)
  if (sep <= 0) return null
  const colId = key.slice(sep + SEP.length)
  return colId ? { rowId: key.slice(0, sep), colId } : null
}

export function asTableConfig(raw: unknown): TableConfig | null {
  return raw && typeof raw === 'object' && Array.isArray((raw as TableConfig).columns)
    ? (raw as TableConfig)
    : null
}

/**
 * How a saved mapping's target reads in the Mappings table.
 *
 * A flat field is checked first, so a field id that happens to contain `__` is
 * never read as a cell. A cell resolves to `Field → Row / Column`, the same
 * string the backend puts on a proposal. `stale` means the row or column is no
 * longer on the form: the backend skips such a mapping instead of writing it.
 */
export function describeMappingTarget(
  def: DefinitionLike | null | undefined,
  targetFieldId: string,
): { display: string; stale: boolean } {
  if (!def) return { display: targetFieldId, stale: false }
  const fields = allFields(def)
  const flat = fields.find((f) => f.id === targetFieldId)
  if (flat) return { display: flat.label, stale: false }

  const parts = splitCellKey(targetFieldId)
  if (!parts) {
    // Matches no flat field and is not a cell key, so the target is gone and
    // the backend will skip the mapping rather than write it — the tab has to
    // say so. The one exception is the reserved added-rows key, which holds
    // respondent-added rows and is absent from the field list by design.
    return { display: targetFieldId, stale: targetFieldId !== ADDED_ROWS_KEY }
  }

  // Same search order as the backend: a whole-form grid first, then each table field.
  const grids: Array<{ fieldLabel: string; config: TableConfig }> = []
  const own = asTableConfig((def as { tableConfig?: unknown }).tableConfig)
  if (own) grids.push({ fieldLabel: '', config: own })
  for (const f of fields) {
    const config = f.fieldType === 'table' ? asTableConfig(f.tableConfig) : null
    if (config) grids.push({ fieldLabel: f.label || 'Table', config })
  }

  for (const { fieldLabel, config } of grids) {
    const col = config.columns.find((c) => c.id === parts.colId)
    const row = (config.rows ?? []).find((r) => r.id === parts.rowId)
    if (col && row) {
      const cell = `${row.label || 'Row'} / ${col.label || 'Column'}`
      return { display: fieldLabel ? `${fieldLabel} → ${cell}` : cell, stale: false }
    }
  }
  // Name the table the cell belonged to when either half still points at one.
  const owner = grids.find(
    ({ config }) =>
      config.columns.some((c) => c.id === parts.colId) ||
      (config.rows ?? []).some((r) => r.id === parts.rowId),
  )
  return { display: owner?.fieldLabel || targetFieldId, stale: true }
}

/** A mapping's condition as `key = value`, pairs joined by commas; `—` when empty. */
export function formatSelector(selector: Record<string, unknown> | null | undefined): string {
  const entries = Object.entries(selector ?? {})
  if (entries.length === 0) return '—'
  return entries.map(([k, v]) => `${k} = ${String(v)}`).join(', ')
}

/**
 * A typed condition value as the scalar the backend compares.
 *
 * Numifies only a value that round-trips, so `''` stays `''` (bare `Number('')`
 * is 0) and a zero-padded id like `'0012'` stays a string.
 */
export function parseSelectorValue(raw: string): string | number | boolean {
  const trimmed = raw.trim()
  if (trimmed === 'true') return true
  if (trimmed === 'false') return false
  if (trimmed !== '' && String(Number(trimmed)) === trimmed) return Number(trimmed)
  return trimmed
}

/**
 * Condition rows as a selector. A row missing its field or its value is an
 * unfinished row and is dropped: a condition on an empty value would match
 * almost nothing. The last duplicate key wins.
 */
/**
 * Whether any condition row is HALF filled in.
 *
 * `buildSelector` drops a row unless both halves are present, which silently
 * widened a mapping the operator meant to narrow: typing a key and leaving its
 * value blank produced no selector at all, and a mapping with no selector
 * matches every result the instrument sends. A row with both halves empty is
 * just an unused slot and stays allowed.
 *
 * Separate from `buildSelector` so the refusal belongs to the caller, which is
 * where an operator can be told about it.
 */
export function hasIncompleteCondition(
  pairs: Array<{ key: string; value: string }>,
): boolean {
  return pairs.some(({ key, value }) => {
    const k = key.trim()
    const v = value.trim()
    return (k === '') !== (v === '')
  })
}

export function buildSelector(
  pairs: Array<{ key: string; value: string }>,
): Record<string, unknown> | undefined {
  const selector: Record<string, unknown> = {}
  for (const { key, value } of pairs) {
    const k = key.trim()
    if (k && value.trim()) selector[k] = parseSelectorValue(value)
  }
  return Object.keys(selector).length > 0 ? selector : undefined
}
